import type { ReactNode } from 'react'
import { MessageImages } from './ImageAttach'

/* 反馈对话的视觉体系(Apple 风):#f5f5f7 页面底、白卡、#1d1d1f 正文、#86868b 次要字;
   用户气泡 iMessage 灰 #f2f2f7;官方气泡低饱和品牌红淡色底(不用纯黑块)。 */
export const FB = {
  page: '#f5f5f7',
  ink: '#1d1d1f',
  muted: '#86868b',
  userBubble: '#eeeef2',
  adminBubble: 'rgba(219, 49, 66, 0.11)',
  adminEdge: 'rgba(219, 49, 66, 0.22)',
  brand: '#DB3142',
  gold: '#EAB13B',
}

const STATUS: Record<string, { text: string; bg: string; fg: string }> = {
  open: { text: '待处理', bg: 'rgba(234, 177, 59, 0.16)', fg: '#9a6b00' },
  replied: { text: '已回复', bg: 'rgba(52, 199, 89, 0.14)', fg: '#1f7a3a' },
  resolved: { text: '已解决', bg: 'rgba(0, 122, 255, 0.12)', fg: '#0a5ec2' },
  closed: { text: '已关闭', bg: '#f2f2f7', fg: '#86868b' },
}

export const CATEGORY_LABEL: Record<string, string> = {
  suggestion: '建议',
  bug: '问题反馈',
  praise: '表扬',
  other: '其他',
}

/** 小胶囊标签(分类/关联/状态) */
export const Pill = ({ children, bg = '#f2f2f7', fg = FB.ink, className = '' }: { children: ReactNode; bg?: string; fg?: string; className?: string }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium leading-5 ${className}`} style={{ background: bg, color: fg }}>
    {children}
  </span>
)

export const StatusPill = ({ status }: { status: string }) => {
  const s = STATUS[status] || STATUS.open
  return (
    <Pill bg={s.bg} fg={s.fg}>
      {s.text}
    </Pill>
  )
}

export const CategoryPill = ({ category }: { category?: string | null }) => (category ? <Pill>{CATEGORY_LABEL[category] || category}</Pill> : null)

/** 金色五星(只显示已点亮的) */
export const Stars = ({ value }: { value?: number | null }) =>
  value ? (
    <span className="inline-flex items-center gap-0.5 text-sm leading-none" style={{ color: FB.gold }} aria-label={`评分 ${value}`}>
      {Array.from({ length: value }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i}>★</span>
      ))}
    </span>
  ) : null

const fmtTime = (iso?: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getMonth() + 1}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** 一条对话气泡。mine=true 靠右。官方气泡带「灵缇官方」小标。 */
export const MessageBubble = ({
  role,
  mine,
  content,
  images,
  time,
  imageSize = 96,
}: {
  role: 'user' | 'admin' | string
  mine: boolean
  content?: string
  images?: string[]
  time?: string
  imageSize?: number
}) => {
  const admin = role === 'admin'
  return (
    <div className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
      <div
        className="max-w-[78%] px-3.5 py-2.5 text-[15px] leading-relaxed"
        style={{
          background: admin ? FB.adminBubble : FB.userBubble,
          boxShadow: admin ? `inset 0 0 0 1px ${FB.adminEdge}` : undefined,
          color: FB.ink,
          borderRadius: 18,
          [mine ? 'borderBottomRightRadius' : 'borderBottomLeftRadius']: 6,
        }}
      >
        <div className="mb-0.5 text-[11px] font-semibold" style={{ color: admin ? FB.brand : FB.muted }}>
          {admin ? '灵缇官方' : '用户'}
        </div>
        {content && <div className="whitespace-pre-wrap break-words">{content}</div>}
        <MessageImages images={images} size={imageSize} />
      </div>
      {time && (
        <div className="mt-1 px-1 text-[11px]" style={{ color: FB.muted }}>
          {fmtTime(time)}
        </div>
      )}
    </div>
  )
}

/** 公开反馈墙里的官方答复块 */
export const OfficialReply = ({ text }: { text: string }) => (
  <div
    className="mt-2.5 whitespace-pre-wrap px-3.5 py-2.5 text-[15px] leading-relaxed"
    style={{ background: FB.adminBubble, color: FB.ink, borderRadius: 14, borderLeft: `3px solid ${FB.brand}`, boxShadow: `inset 0 0 0 1px ${FB.adminEdge}` }}
  >
    <span className="mr-2 text-xs font-semibold" style={{ color: FB.brand }}>
      灵缇官方
    </span>
    {text}
  </div>
)

/** 对话输入区:圆角灰底文本框 + 品牌红胶囊按钮 */
export const composerTextareaStyle = {
  background: FB.userBubble,
  border: 'none',
  borderRadius: 14,
  boxShadow: 'none',
  padding: '10px 14px',
  fontSize: 15,
  color: FB.ink,
} as const

export const brandButtonStyle = {
  background: FB.brand,
  borderColor: FB.brand,
  color: '#fff',
  borderRadius: 9999,
  height: 40,
  padding: '0 20px',
  fontWeight: 600,
  boxShadow: '0 4px 12px rgba(219, 49, 66, 0.25)',
} as const

export const softButtonStyle = {
  background: FB.userBubble,
  borderColor: 'transparent',
  color: FB.ink,
  borderRadius: 9999,
  boxShadow: 'none',
} as const
